<template>
    <v-app id="app">
        <!-- App bar -->
        <v-app-bar app clipped-right color="bal-gray" light flat>
            <v-app-bar-nav-icon large @click.stop="toggleDrawer" />
            <v-toolbar-title
                >{{ `${$route.meta.title}` | t
                }}<span style="color:gray;">{{ ` (${clientCount})` }}</span></v-toolbar-title
            >
            <v-combobox
                v-if="hasPermissionForAction(PermissionsConstants.CLIENT, PermissionsConstants.SEARCH)"
                :items="searchHistory"
                :value="searchClientString"
                @input="controlSearchString($event)"
                item-text="keyword"
                id="searchInput"
                class="mt-6 mx-2"
                :placeholder="'Client name, country, address, comment' | t"
                prepend-inner-icon="search"
                flat
                solo
                dense
                clearable
            >
                <template slot="item" slot-scope="data">
                    <span
                        @click="updateClientSearch(data.item.keyword)"
                        style="width: 100%; height: 100%"
                        class="pa-2"
                        >{{ data.item.keyword }}</span
                    >
                </template>
            </v-combobox>
            <v-spacer v-else />

            <v-menu bottom left offset-y v-if="user.role === 'superadmin'">
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" class="px-2 ml-2 mx-2" color="bal-dark-gray" dark style="min-width: 0;">
                        <v-icon>{{ "fas fa-file-export" }}</v-icon>
                    </v-btn>
                </template>
                <v-list style="width: 160px;">
                    <v-list-item @click="toggleExportExcel">
                        <v-list-item-title style="color: red; text-align: center;"
                            >{{ "Export" | t }}
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item>
                        <v-file-input
                            show-size
                            hide-details
                            dense
                            accept=".xlsx, .xls"
                            :label="'Upload' | t"
                            v-model="fileUploadValue"
                            @change="uploadExcel($event)"
                        />
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-btn
                dark
                color="bal-dark-gray"
                class="px-2"
                v-if="hasPermissionForAction(PermissionsConstants.CLIENT, PermissionsConstants.FILTER)"
                @click.stop="toggleListSettingsModal"
                style="min-width: 0;"
            >
                <v-icon>settings</v-icon>
            </v-btn>
            <v-btn
                dark
                color="bal-red"
                class="px-2 ml-2"
                style="min-width: 0;"
                v-if="hasPermissionForAction(PermissionsConstants.CLIENT, PermissionsConstants.CREATE)"
                @click.stop="openNewClientDrawer"
            >
                <v-icon>add</v-icon>
            </v-btn>
        </v-app-bar>

        <!-- Drawer left -->
        <main-drawer :drawer-open="mainDrawerOpen" />

        <!--    Right drawer    -->
        <v-navigation-drawer :value="drawerOpen" fixed right hide-overlay width="900">
            <client-view v-if="$route.name === 'client-view'"></client-view>
            <client-edit v-if="$route.name === 'client-edit'"></client-edit>
        </v-navigation-drawer>

        <!-- Main content -->
        <v-main>
            <router-view />
        </v-main>

        <!-- Footer -->
        <page-footer />
        <client-list-settings-modal />

        <v-snackbar color="success" bottom :timeout="2500" :value="requestSuccess">
            <div style="display: flex; flex-direction: row;">
                <v-icon color="#FFF">check</v-icon>
                <div class="title pa-2 ">{{ "Success" | t }}</div>
            </div>
        </v-snackbar>

        <!-- Error snackbar -->
        <v-snackbar color="error" bottom :timeout="2500" :value="requestError">
            <div style="display: flex; flex-direction: row;">
                <v-icon color="#FFF">error</v-icon>
                <div class="title pa-2 ">{{ errorMessage }}</div>
            </div>
        </v-snackbar>
    </v-app>
</template>
<script>
import MainDrawer from "../components/main-drawer.vue";
import PageFooter from "../components/page-footer.vue";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import Store from "../store";
import ClientView from "@/pages/clients/client-view";
import ClientEdit from "@/pages/clients/client-edit";
import ClientListSettingsModal from "@/pages/clients/client-list-settings-modal";
import { PermissionsConstants } from "@/permissions";
import settings from "@/settings";

export default {
    name: "client",
    components: {
        ClientListSettingsModal,
        ClientEdit,
        ClientView,
        PageFooter,
        MainDrawer
    },
    data() {
        return {
            mainDrawerOpen: null,
            fileUploadValue: null
        };
    },
    async created() {
        await Store.dispatch("SearchHistoryStore/getSearchHistory", this.$route.path.replace("/", ""));
    },
    async mounted() {
        document.addEventListener("keydown", this.handleKeyboardListener);
        const drawerState = localStorage.getItem("mainDrawerOpen");
        if (drawerState) {
            this.mainDrawerOpen = JSON.parse(drawerState);
        } else {
            this.mainDrawerOpen = true;
        }
        await this.getFrequentlyUsedQueries({ shops: true });
    },
    destroyed() {
        if (this.searchClientString.length > 0) Store.commit("ClientStore/resetClientSearch");
        document.removeEventListener("keydown", this.handleKeyboardListener);
    },
    watch: {
        requestSuccess: value => {
            if (value) setTimeout(() => Store.commit("ClientStore/setRequestSuccess", false), 2500);
        },
        requestError: value => {
            if (value)
                setTimeout(() => {
                    Store.commit("ClientStore/setRequestError", false);
                    Store.commit("ClientStore/setErrorMessage", "");
                }, 2500);
        }
    },
    methods: {
        ...mapActions(["getFrequentlyUsedQueries"]),
        ...mapActions("ClientStore", ["importExcel"]),
        ...mapMutations("ClientStore", ["toggleListSettingsModal"]),
        openNewClientDrawer() {
            this.$router.push({ name: "client-edit", params: { clientId: "new" } });
        },
        updateClientSearch(value) {
            Store.commit("ClientStore/updateClientSearch", { term: value });
        },
        toggleDrawer() {
            this.mainDrawerOpen = !this.mainDrawerOpen;
            localStorage.setItem("mainDrawerOpen", this.mainDrawerOpen);
        },
        handleKeyboardListener(e) {
            if (e.keyCode === 27 && this.imageFileDialog) {
                Store.commit("ClientStore/setImageFileDialog", false);
            } else if (e.keyCode === 27 && this.drawerOpen) {
                this.$router.push({ name: "client-list" });
            } else if (e.keyCode === 13 && document.activeElement.tagName === "INPUT") {
                Store.commit("ClientStore/updateClientSearch", {
                    term: document.querySelector("#searchInput").value
                });
            }
            if (
                this.activeClient &&
                Object.keys(this.activeClient).length > 0 &&
                this.activeClient.id !== "new" &&
                this.$route.name !== "client-edit"
            ) {
                let currentUserIndex = this.clients.findIndex(p => p.id === this.activeClient.id);
                if (e.key === "ArrowUp") {
                    if (currentUserIndex !== 0) {
                        const newClientId = this.clients[currentUserIndex - 1].id;
                        Store.dispatch("ClientStore/getClientData", newClientId);
                        this.$router.push({ name: this.$route.name, params: { clientId: newClientId } });
                    }
                } else if (e.key === "ArrowDown") {
                    if (currentUserIndex + 1 !== this.clients.length) {
                        const newClientId = this.clients[currentUserIndex + 1].id;
                        Store.dispatch("ClientStore/getClientData", newClientId);
                        this.$router.push({ name: this.$route.name, params: { clientId: newClientId } });
                    }
                }
            }
        },
        controlSearchString(value) {
            if (value == "") {
                Store.dispatch("SearchHistoryStore/getSearchHistory", this.$route.path.replace("/", ""));
                this.updateClientSearch(value);
            }
        },
        toggleExportExcel() {
            if (confirm("Are you sure?")) {
                window.location.href = settings.api_url + "/clients-excel/";
            }
        },
        async uploadExcel(e) {
            if (e) {
                await this.importExcel(e);
                this.fileUploadValue = null;
            }
        }
    },
    computed: {
        ...mapState(["user"]),
        ...mapState("ClientStore", [
            "drawerOpen",
            "searchClientString",
            "requestSuccess",
            "activeClient",
            "clients",
            "imageFileDialog",
            "requestError",
            "errorMessage"
        ]),
        ...mapGetters("ClientStore", ["clientCount"]),
        ...mapState("SearchHistoryStore", ["searchHistory"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        }
    }
};
</script>

<style scoped>
.v-input__icon--prepend-inner .v-icon {
    margin-top: 0px !important;
}
</style>
